import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'; // <-- Import hinzugefügt

type TokenInputProps = {
  value: string;
  onSubmit: (finalValue: string) => void;
  onChange?: (value: string) => void;
  isLoading?: boolean;
};

const TOKEN_REGEX = /\[\[INPUT:\s*"?([^"\]]+)"?\s*\]\]/g;

export default function TokenInput({ value, onSubmit, onChange, isLoading }: TokenInputProps) {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);

  const extractFinalValue = () => {
    const el = editorRef.current;
    if (!el) return '';

    let result = '';

    el.childNodes.forEach(node => {
      if (node instanceof HTMLElement && node.dataset?.inputName !== undefined) {
        result += node.innerText.trim();
      } else {
        result += node.textContent;
      }
    });

    return result;
  };

  // 🔧 Build DOM
  useEffect(() => {
    if (!editorRef.current) return;

    const currentValue = extractFinalValue();
    if (currentValue === value) return;

    const el = editorRef.current;
    el.innerHTML = '';

    const parts = value.split(TOKEN_REGEX);

    parts.forEach((part, i) => {
      if (i % 2 === 1) {
        const span = document.createElement('span');

        span.contentEditable = 'true';
        span.dataset.inputName = part;
        span.dataset.placeholder = part;

        span.className = `
          token-input
          inline-block
          min-w-[80px]
          px-2 py-1
          mx-0.5
          rounded-md
          bg-white
          border border-slate-300
          text-sm
          text-black
          outline-none
        `;

        el.appendChild(span);
      } else if (part) {
        el.appendChild(document.createTextNode(part));
      }
    });
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        onSubmit(extractFinalValue());
      }
    }

    if (e.key === 'Backspace') {
      if (!selection || !anchorNode) return;

      const parent = anchorNode.parentElement;
      if (parent?.dataset?.inputName) {
        if (parent.textContent === '') {
          e.preventDefault();
          parent.remove();
        }
        return;
      }

      const range = selection.getRangeAt(0);
      if (range.startOffset === 0) {
        const prev = anchorNode.previousSibling;

        if (prev instanceof HTMLElement && prev.dataset?.inputName) {
          e.preventDefault();
          prev.remove();
        }
      }
    }
  };

  const handleInput = () => {
    onChange?.(extractFinalValue());
  };

  return (
    <>
      <style>
        {`
          .token-editor:empty:before {
            content: attr(data-placeholder);
            color: var(--color-slate-300);
            pointer-events: none;
          }

          .token-input:empty:before {
            content: attr(data-placeholder);
            color: var(--color-slate-300);
            pointer-events: none;
          }
        `}
      </style>

      <div
        ref={editorRef}
        contentEditable
        data-placeholder={t('TokenInput.placeholder')}
        suppressContentEditableWarning
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        className="token-editor flex-1 min-w-0 max-w-full text-sm focus:outline-none min-h-[24px] py-1.5"
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          maxHeight: '90px',
          overflowY: 'auto',
        }}
      />
    </>
  );
}
